import {mapStore} from '@js/stores/mapStore';
import {letBrowserHandleInput} from '@js/modules/utils';
import {pushError} from '@js/actions/errorManagement';

// A map that never becomes usable (no WebGL, failed chunk, stuck render) must not keep these panels away.
export const MAP_USABLE_MAX_WAIT = 10000;

// The page map is usable (MapInterface.onMapLoaded), or maxWait has passed.
const whenMapUsable = (maxWait) => new Promise((resolve) => {
    let map = null;
    let unsubscribe = null;

    const stopListening = () => {
        unsubscribe?.();
        unsubscribe = null;
    };
    const fallback = setTimeout(() => {
        stopListening();
        resolve();
    }, maxWait);
    const waitForMap = () => {
        stopListening();
        map.onMapLoaded(() => {
            clearTimeout(fallback);
            resolve();
        });
    };

    unsubscribe = mapStore.subscribe((state) => {
        if (map || !state.map) {
            return;
        }

        map = state.map;
        // The first call runs inside subscribe(), before unsubscribe exists: handled right below.
        if (unsubscribe) {
            waitForMap();
        }
    });

    if (map) {
        waitForMap();
    }
});

// The map's boot keeps the main thread busy: these mounts come after it, one per task, so a tap is never
// stuck behind them. Each starts without waiting for the previous one's chunk; a failure is reported only.
export const mountAfterMapUsable = async (mounts, {maxWait = MAP_USABLE_MAX_WAIT} = {}) => {
    await whenMapUsable(maxWait);

    /* eslint-disable no-await-in-loop -- sequential on purpose: one mount per task */
    for (const mountComponent of mounts) {
        await letBrowserHandleInput();

        try {
            mountComponent().catch(pushError);
        } catch (error) {
            pushError(error);
        }
    }
    /* eslint-enable no-await-in-loop */
};

// Starts the import now (one retry); the returned loader imports again if both attempts were rejected.
export const importEarly = (factory) => {
    const early = factory().catch(() => factory());

    return () => early.catch(() => factory());
};
